import { getHiveflowConfig } from "@/lib/environment";

/**
 * Tiny IndexedDB-backed queue for offline POSTs.
 * Used by InspectionForm to queue inspections submitted while offline.
 * On reconnect (or manual flush), entries are POSTed sequentially.
 */
const DB_NAME = "imker_offline";
const STORE = "queue";
const VERSION = 1;

function currentOwnerKey() {
  try {
    const user = JSON.parse(localStorage.getItem("imker_user") || "null");
    return user?.id ? `${user.organization_id || user.id}:${user.id}` : null;
  } catch {
    return null;
  }
}

function openDB() {
  const config = getHiveflowConfig();
  if (config.offlineBundle || (config.nativeBridgeEnabled && typeof window !== "undefined" && window.ReactNativeWebView)) {
    return Promise.reject(new Error("IndexedDB-queue is uitgeschakeld in de native WebView"));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode) {
  const db = await openDB();
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueue(entry) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const r = store.add({
      ...entry,
      owner_key: entry.owner_key || currentOwnerKey(),
      client_action_id: entry.client_action_id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      queued_at: new Date().toISOString(),
    });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export async function listQueue() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const r = store.getAll();
    r.onsuccess = () => {
      const ownerKey = currentOwnerKey();
      resolve((r.result || []).filter((entry) => ownerKey && entry.owner_key === ownerKey));
    };
    r.onerror = () => reject(r.error);
  });
}

export async function removeEntry(id) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const r = store.delete(id);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

export async function queueSize() {
  return (await listQueue()).length;
}
